/**
 * The campaign builder's tree layout, shared so a campaign graph built outside the builder (the MCP
 * sequence converter, PREC-4258) lands where the builder would put it. The web builder calls this
 * through `autoLayoutHorizontal`.
 */

/** A node the layout can place: anything with an id and a `data` object it may add `level` to. */
export interface LayoutNode {
	id: string;
	data?: object;
}

/** An edge the layout reads: parentage and the branch handle, nothing else. */
export interface LayoutEdge {
	source: string;
	target: string;
	sourceHandle?: string | null;
	type?: string;
}

/** Horizontal distance between two adjacent leaf slots — a node card plus breathing room. */
const X_GAP = 550;

/** Vertical distance between two consecutive steps. */
const Y_GAP = 260;

/**
 * Where the first root sits. Kept on the axis the previous layer-centred layout used
 * (`(idx - layerSize / 2) * X_GAP`), so a straight chain and a symmetric yes/no split keep the
 * exact coordinates campaigns already have stored in `list.campaignConfig`.
 */
export const CAMPAIGN_LAYOUT_ROOT_X = -275;

/** Empty slots between two disconnected trees, so an orphan chain gets its own band. */
const TREE_GUTTER = 1;

/** Branches read left to right: the yes side first, the no side last. */
const BRANCH_ORDER: Record<string, number> = { yesEdge: 0, normalEdge: 1, noEdge: 2 };

interface Placement {
	slot: number;
	depth: number;
}

/**
 * Lays the campaign graph out as a tidy tree: every step sits one row below its parent, every
 * parent is centred over its own children, and each subtree owns a horizontal band of its own —
 * so branches can never cross or overlap, however lopsided they are.
 *
 * Parentage comes from `edges` and nothing else. `data.source` / `data.edgeType` mirror the graph
 * and go stale: `handleDeleteNode` rewires the edges and re-runs the layout before the effect that
 * refreshes those fields, so a child of a deleted step still pointed at the step that was removed.
 * The old comparator resolved both sides of such a pair to `-1` and returned `1` for the tie,
 * leaving the row in raw creation order — which is how two branches ended up swapped, and then
 * persisted (list 6839's campaign shipped with its two email steps under each other's parent).
 */
export function layoutCampaignGraph<N extends LayoutNode>(
	nodes: N[],
	edges: LayoutEdge[]
): (N & { data: N["data"] & { level: number }; position: { x: number; y: number } })[] {
	const childrenOf = buildChildLists(nodes, edges);
	const placements = new Map<string, Placement>();

	let nextSlot = 0;

	/** Post-order: leaves take the next free slot, a parent takes the middle of its children. */
	function place(nodeId: string, depth: number): number | null {
		if (placements.has(nodeId)) {
			return null;
		}

		// Reserved before recursing so a cycle in stored data terminates instead of overflowing.
		placements.set(nodeId, { slot: 0, depth });

		const childSlots = (childrenOf.get(nodeId) ?? [])
			.map((childId) => place(childId, depth + 1))
			.filter((slot): slot is number => slot !== null);

		const slot = childSlots.length
			? (childSlots[0] + childSlots[childSlots.length - 1]) / 2
			: nextSlot++;

		placements.set(nodeId, { slot, depth });

		return slot;
	}

	function placeTree(nodeId: string) {
		const slotsBefore = nextSlot;

		place(nodeId, 0);

		if (nextSlot > slotsBefore) {
			nextSlot += TREE_GUTTER;
		}
	}

	const roots = findRoots(nodes, edges);

	for (const root of roots) {
		placeTree(root);
	}

	// Anything left is unreachable from a root (a cycle with no entry point); give it its own band
	// rather than dropping it on top of the tree at depth 0.
	for (const node of nodes) {
		if (!placements.has(node.id)) {
			placeTree(node.id);
		}
	}

	// The reachable tree keeps the familiar axis; disconnected chains are what gets pushed sideways.
	const originSlot = placements.get(roots[0] ?? nodes[0]?.id ?? "")?.slot ?? 0;

	return nodes.map((node) => {
		const { slot, depth } = placements.get(node.id) ?? { slot: 0, depth: 0 };

		return {
			...node,
			data: { ...node.data, level: depth },
			position: { x: (slot - originSlot) * X_GAP + CAMPAIGN_LAYOUT_ROOT_X, y: depth * Y_GAP },
		};
	}) as (N & { data: N["data"] & { level: number }; position: { x: number; y: number } })[];
}

/**
 * Children per node, ordered yes → normal → no, then by their position in `nodes` so a parent with
 * two same-handle edges still lays out the same way on every run. Edges pointing at nodes the graph
 * no longer holds are dropped: the callers re-layout mid-mutation, before `sanitizeEdges` runs.
 */
function buildChildLists(nodes: LayoutNode[], edges: LayoutEdge[]): Map<string, string[]> {
	const indexOf = new Map(nodes.map((node, index) => [node.id, index]));
	const branches = new Map<string, { target: string; rank: number }[]>();

	for (const edge of edges) {
		const isUsable =
			edge.source !== edge.target && indexOf.has(edge.source) && indexOf.has(edge.target);

		if (!isUsable) {
			continue;
		}

		const siblings = branches.get(edge.source) ?? [];

		if (siblings.some((sibling) => sibling.target === edge.target)) {
			continue;
		}

		siblings.push({ target: edge.target, rank: branchRank(edge) });
		branches.set(edge.source, siblings);
	}

	const childrenOf = new Map<string, string[]>();

	for (const [parentId, siblings] of Array.from(branches.entries())) {
		siblings.sort((a, b) => a.rank - b.rank || indexOf.get(a.target)! - indexOf.get(b.target)!);

		childrenOf.set(
			parentId,
			siblings.map((sibling) => sibling.target)
		);
	}

	return childrenOf;
}

function branchRank(edge: LayoutEdge): number {
	const handle = String(edge.sourceHandle ?? edge.type ?? "normalEdge");

	return BRANCH_ORDER[handle] ?? BRANCH_ORDER.normalEdge;
}

/** Nodes nothing points at, in the order they appear — the starter first, then any orphan chain. */
function findRoots(nodes: LayoutNode[], edges: LayoutEdge[]): string[] {
	const nodeIds = new Set(nodes.map((node) => node.id));
	const hasParent = new Set(
		edges
			.filter((edge) => edge.source !== edge.target && nodeIds.has(edge.source))
			.map((edge) => edge.target)
	);

	return nodes.map((node) => node.id).filter((nodeId) => !hasParent.has(nodeId));
}
